import z from 'zod';
import { BUSINESS_LAW, LABOR_LAW } from './lawTypesContants';
export const CAPABILITIES = {
    ABO: 'Gestion de l\'abonnement du cabinet '
        + '(consultation, modification de formule, '
        + 'moyens de paiement, produits additionnels)',
    API: 'Création entreprises via API',
    ASSIST: 'Accès à dokia.bot (assistant IA)',
    AVIS: 'Avis (création, modification et suppression de ses propres avis)',
    AVO: 'Consultation d\'avocat',
    BASE: 'Gestion base clients/salariés',
    BLACCESS: 'Accès au domaine (Droit des Sociétés)',
    BLACTU: 'Consultation des Actualités (Droit des Sociétés)',
    BLBOT: 'Accès au bot (Droit des Sociétés)',
    BLBUSS: 'Business (Droit des Sociétés)',
    BLCAL: 'Calendriers interactifs (Droit des Sociétés)',
    BLCASE: 'Consultation et gestion des dossiers (Droit des Sociétés)',
    BLDOCB: 'Consultation des documents business (Droit des Sociétés)',
    BLDOCD: 'Consultation des documents (domaine affecté) (Droit des Sociétés)',
    BLEDIT: 'Fonctionnalités d\'édition (Droit des Sociétés)',
    BLMARK: 'Édition en marque blanche (Droit des Sociétés)',
    BLPROC: 'Consultation des procédures (Droit des Sociétés)',
    BLSDG: 'Suggestion document générique (sous 24h) (Droit des Sociétés)',
    BLTMPL: 'Création de modèles de document (Droit des Sociétés)',
    CCN: 'Consultation des Conventions Collectives Nationales',
    CECS: 'Création entreprises clientes et salariés',
    CHRT: 'Gestion du compte cabinet (charte graphique)',
    CLIFI: 'Portail clients finaux (post-V2)',
    DDSA: 'Demande de devis sous-traitance avocat',
    DRH: 'Diagnostic RH (post-V2)',
    DRTS: 'Affectation des droits (domaines, rôles)',
    FAC: 'Gestion des factures',
    FAV: 'Ajouter et consulter ses favoris',
    IMPT: 'Import initial base clients/salariés',
    LLACCESS: 'Accès au domaine (Droit du Travail)',
    LLACTU: 'Consultation des Actualités (Droit du Travail)',
    LLBOT: 'Accès au bot (Droit du Travail)',
    LLBUSS: 'Business (Droit du Travail)',
    LLCAL: 'Calendriers interactifs (Droit du Travail)',
    LLCASE: 'Consultation et gestion des dossiers (Droit du Travail)',
    LLDOCB: 'Consultation des documents business (Droit du Travail)',
    LLDOCD: 'Consultation des documents (domaine affecté) (Droit du Travail)',
    LLEDIT: 'Fonctionnalités d\'édition (Droit du Travail)',
    LLMARK: 'Édition en marque blanche (Droit du Travail)',
    LLPROC: 'Consultation des procédures (Droit du Travail)',
    LLSDG: 'Suggestion document générique (sous 24h) (Droit du Travail)',
    LLTMPL: 'Création de modèles de document (Droit du Travail)',
    MCPT: 'Gestion de mon compte',
    UTIL: 'Gestion des utilisateurs / de l\'équipe du cabinet '
        + '(créer, modifier, suspendre ou supprimer des utilisateurs ; '
        + 'attribution des rôles)',
};
const capabilityValues = Object.keys(CAPABILITIES);
export const capabilityValue = z.enum(capabilityValues);
export const capability = (value) => capabilityValue.parse(value);
export const isCapability = (value) => capabilityValue.safeParse(value).success;
export const hasCapability = (userCapabilities, capability) => {
    return userCapabilities.includes(capability);
};
export const resolveEditCapability = (lawType) => {
    return lawType === 'business_law' ? 'BLEDIT' : 'LLEDIT';
};
export const resolveDocumentConsultationCapabilities = (lawType) => {
    return lawType === 'business_law'
        ? ['BLDOCB', 'BLDOCD']
        : ['LLDOCB', 'LLDOCD'];
};
export const resolveWhiteLabelCapability = (lawType) => {
    return lawType === 'business_law' ? 'BLMARK' : 'LLMARK';
};
export const resolveCaseCapability = (lawType) => {
    return lawType === 'business_law' ? 'BLCASE' : 'LLCASE';
};
export const hasCaseAccess = (userCapabilities, lawType) => {
    return hasCapability(userCapabilities, resolveCaseCapability(lawType));
};
export const hasEditAccess = (userCapabilities, lawType) => {
    return hasCapability(userCapabilities, resolveEditCapability(lawType));
};
export const resolveCalendarCapability = (lawType) => {
    return lawType === 'business_law' ? 'BLCAL' : 'LLCAL';
};
export const resolveTemplateCapability = (lawType) => {
    return lawType === 'business_law' ? 'BLTMPL' : 'LLTMPL';
};
export const hasCalendarAccess = (userCapabilities, lawType) => {
    return hasCapability(userCapabilities, resolveCalendarCapability(lawType));
};
export const hasFirmDocumentTemplateWriteAccess = (userCapabilities, lawType) => {
    return hasCapability(userCapabilities, resolveTemplateCapability(lawType));
};
export const hasAnyFirmDocumentTemplateWriteAccess = (userCapabilities) => {
    return hasCapability(userCapabilities, 'LLTMPL')
        || hasCapability(userCapabilities, 'BLTMPL');
};
export const hasDocumentConsultationAccess = (userCapabilities, lawType) => {
    return resolveDocumentConsultationCapabilities(lawType).some(capability => hasCapability(userCapabilities, capability));
};
export const hasEditorPreviewAccess = (userCapabilities, lawType) => {
    return hasEditAccess(userCapabilities, lawType)
        || hasDocumentConsultationAccess(userCapabilities, lawType);
};
export const hasWhiteLabelAccess = (userCapabilities, lawType) => {
    return hasCapability(userCapabilities, resolveWhiteLabelCapability(lawType));
};
export const resolveSelectedDomainForCapabilities = (userCapabilities, selectedDomain) => {
    if (selectedDomain === LABOR_LAW
        && !hasCapability(userCapabilities, 'LLACCESS')) {
        return BUSINESS_LAW;
    }
    return selectedDomain;
};
export const hasAccessToManagementSubMenu = (userCapabilities, subMenu) => {
    switch (subMenu) {
        case 'CORPORATE_IDENTITY': {
            return userCapabilities.includes('CHRT');
        }
        case 'INVOICES': {
            return userCapabilities.includes('FAC');
        }
        case 'SUBSCRIPTION': {
            return userCapabilities.includes('ABO');
        }
        case 'TEAM': {
            return userCapabilities.includes('UTIL');
        }
        default: {
            return false;
        }
    }
};
export const hasAccessToPartnersSubMenu = (userCapabilities, subMenu, lawType) => {
    switch (subMenu) {
        case 'LAWYER_CONSULTATION': {
            return lawType === 'labor_law' && userCapabilities.includes('AVO');
        }
        case 'REQUEST_QUOTE': {
            return lawType === 'labor_law' && userCapabilities.includes('DDSA');
        }
        case 'SUGGEST_DOCUMENT': {
            const capability = lawType === 'business_law' ? 'BLSDG' : 'LLSDG';
            return userCapabilities.includes(capability);
        }
        default: {
            return false;
        }
    }
};
export const hasAccessTo = (userCapabilities, menu, lawType) => {
    const prefix = lawType === 'business_law' ? 'BL' : 'LL';
    switch (menu) {
        // dokia.bot opens and fills cases: it also needs the cases of the domain.
        case 'ASSISTANT': {
            return userCapabilities.includes('ASSIST')
                && userCapabilities.includes(`${prefix}CASE`);
        }
        case 'BOT': {
            return userCapabilities.includes(`${prefix}BOT`);
        }
        case 'BUSINESS': {
            return userCapabilities.includes(`${prefix}BUSS`);
        }
        case 'CASE': {
            return userCapabilities.includes(`${prefix}CASE`);
        }
        case 'CCN': {
            return lawType === 'labor_law' && userCapabilities.includes('CCN');
        }
        case 'CLIENTS': {
            return userCapabilities.includes('BASE');
        }
        case 'CORPORATE_IDENTITY': {
            return userCapabilities.includes('CHRT');
        }
        case 'FAVORITES': {
            return userCapabilities.includes('FAV');
        }
        case 'INVOICES': {
            return userCapabilities.includes('FAC');
        }
        case 'MANAGEMENT': {
            return hasAccessToManagementSubMenu(userCapabilities, 'CORPORATE_IDENTITY')
                || hasAccessToManagementSubMenu(userCapabilities, 'TEAM')
                || hasAccessToManagementSubMenu(userCapabilities, 'SUBSCRIPTION')
                || hasAccessToManagementSubMenu(userCapabilities, 'INVOICES');
        }
        case 'NEWS': {
            return userCapabilities.includes(`${prefix}ACTU`);
        }
        case 'PARTNERS': {
            return userCapabilities.includes('AVO')
                || userCapabilities.includes('DDSA')
                || userCapabilities.includes(`${prefix}SDG`);
        }
        case 'PROC': {
            return userCapabilities.includes(`${prefix}PROC`);
        }
        case 'SUBSCRIPTION': {
            return userCapabilities.includes('ABO');
        }
        case 'TEAM': {
            return userCapabilities.includes('UTIL');
        }
        default: {
            return false;
        }
    }
};
/**
 * Formula / firm-ceiling migration only: if UTIL is present and ABO is
 * absent, grant ABO. Do not apply to manual override lists
 * (enabledPermissions / disabledPermissions).
 */
// UTIL / ABO are capability codes — not "utility" / "about".
// eslint-disable-next-line unicorn/prevent-abbreviations -- catalogue codes
export const grantAboWhenUtilPresent = (capabilities) => {
    if (!capabilities.includes('UTIL')
        || capabilities.includes('ABO')) {
        return [...capabilities];
    }
    return [...capabilities, 'ABO'];
};
//# sourceMappingURL=capabilities.js.map